import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import PageHeader, { PageBody, Card, Badge, useFetch } from "../components/Shared";
import { toast } from "sonner";
import { CalendarBlank, Trash, ArrowLeft, ArrowRight, VideoCamera, ChalkboardTeacher, CheckCircle, Clock, ArrowsClockwise, Gear, Prohibit, Plus, LinkSimple, Copy } from "@phosphor-icons/react";

const KIND_META = {
  interview: { label: "Entrevista", icon: VideoCamera, tone: "terracotta", color: "bg-terracotta" },
  induction: { label: "Capacitación", icon: ChalkboardTeacher, tone: "forest", color: "bg-forest" },
  other: { label: "Otro", icon: CalendarBlank, tone: "neutral", color: "bg-carbon-500" },
};

const STATUS_META = {
  pending: { label: "Pendiente", tone: "terracotta" },
  attended: { label: "Atendida", tone: "forest" },
  done: { label: "Finalizada", tone: "neutral" },
  cancelled: { label: "Cancelada", tone: "neutral" },
};

const WEEKDAYS = [
  { key: "mon", label: "Lunes" },
  { key: "tue", label: "Martes" },
  { key: "wed", label: "Miércoles" },
  { key: "thu", label: "Jueves" },
  { key: "fri", label: "Viernes" },
  { key: "sat", label: "Sábado" },
  { key: "sun", label: "Domingo" },
];

function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString("es-GT", { hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
}
function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("es-GT", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "—"; }
}
function fmtWeekday(iso) {
  try { return new Date(iso).toLocaleDateString("es-GT", { weekday: "long" }); }
  catch { return "—"; }
}
function dayKey(iso) { try { return new Date(iso).toISOString().slice(0, 10); } catch { return ""; } }

function startOfWeek(d) {
  const x = new Date(d); const dow = (x.getDay() + 6) % 7; x.setDate(x.getDate() - dow); x.setHours(0, 0, 0, 0); return x;
}
function endOfWeek(d) { const s = startOfWeek(d); const e = new Date(s); e.setDate(s.getDate() + 6); e.setHours(23, 59, 59, 999); return e; }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999); }

export default function Calendar() {
  const [tab, setTab] = useState("events"); // events | availability | blackouts
  const [view, setView] = useState("week"); // day | week | month
  const [anchor, setAnchor] = useState(new Date());
  const [filterKind, setFilterKind] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);

  const range = useMemo(() => {
    if (view === "day") {
      const s = new Date(anchor); s.setHours(0, 0, 0, 0);
      const e = new Date(anchor); e.setHours(23, 59, 59, 999);
      return { from: s, to: e };
    }
    if (view === "week") return { from: startOfWeek(anchor), to: endOfWeek(anchor) };
    return { from: startOfMonth(anchor), to: endOfMonth(anchor) };
  }, [view, anchor]);

  const { data: events, loading, reload } = useFetch(
    () => api.get("/calendar/events", {
      params: { from: range.from.toISOString(), to: range.to.toISOString(), kind: filterKind || undefined, status: filterStatus || undefined },
    }).then((r) => r.data),
    [range.from.toISOString(), range.to.toISOString(), filterKind, filterStatus]
  );
  const { data: counts, reload: reloadCounts } = useFetch(() => api.get("/calendar/pending-count").then((r) => r.data), []);

  const navigate = (dir) => {
    const d = new Date(anchor);
    if (view === "day") d.setDate(d.getDate() + dir);
    else if (view === "week") d.setDate(d.getDate() + 7 * dir);
    else d.setMonth(d.getMonth() + dir);
    setAnchor(d);
  };

  // Agrupar por día para todas las vistas
  const groupedByDay = useMemo(() => {
    const g = {};
    (events || []).forEach((e) => {
      const k = dayKey(e.scheduled_at);
      (g[k] = g[k] || []).push(e);
    });
    return g;
  }, [events]);

  const headerLabel = useMemo(() => {
    if (view === "day") return fmtDate(anchor);
    if (view === "week") return `${fmtDate(range.from)} – ${fmtDate(range.to)}`;
    return anchor.toLocaleDateString("es-GT", { month: "long", year: "numeric" });
  }, [view, anchor, range.from, range.to]);

  return (
    <>
      <PageHeader
        label="Operación"
        title="Calendario"
        description="Entrevistas y capacitaciones agendadas. Comparte links para que el usuario auto-agende, agrega el link de la reunión y notifícale por WhatsApp."
      >
        <button onClick={() => { reload(); reloadCounts(); }} data-testid="cal-refresh" className="bg-page text-carbon-600 border border-carbon-200 px-3 py-2 rounded text-sm flex items-center gap-2 hover:bg-carbon-50">
          <ArrowsClockwise size={14} /> Refrescar
        </button>
      </PageHeader>

      <PageBody>
        {/* Tabs */}
        <div className="flex items-center gap-1 mb-4 border-b border-carbon-200">
          {[
            { k: "events", label: "Eventos", icon: CalendarBlank },
            { k: "availability", label: "Horarios por servicio", icon: Gear },
            { k: "blackouts", label: "Bloqueos manuales", icon: Prohibit },
            { k: "public", label: "Link público", icon: LinkSimple },
          ].map(({ k, label, icon: Icon }) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`cal-tab-${k}`}
              className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 -mb-px ${tab === k ? "border-terracotta text-terracotta font-medium" : "border-transparent text-carbon-500 hover:text-carbon-700"}`}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {tab === "availability" && <AvailabilityTab />}
        {tab === "blackouts" && <BlackoutsTab />}
        {tab === "public" && <PublicLinkTab />}
        {tab !== "events" ? null : (<>
        {/* Pending counters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <Card className="p-4 border-terracotta/40">
            <div className="flex items-center gap-2"><VideoCamera size={18} className="text-terracotta" /><span className="text-sm font-medium">Entrevistas pendientes</span></div>
            <div className="font-mono text-3xl text-terracotta mt-1" data-testid="cal-pending-interviews">{counts?.interviews ?? "—"}</div>
            <div className="text-xs text-carbon-500">Sin atender por un humano</div>
          </Card>
          <Card className="p-4 border-forest/40">
            <div className="flex items-center gap-2"><ChalkboardTeacher size={18} className="text-forest" /><span className="text-sm font-medium">Capacitaciones pendientes</span></div>
            <div className="font-mono text-3xl text-forest mt-1" data-testid="cal-pending-inductions">{counts?.inductions ?? "—"}</div>
            <div className="text-xs text-carbon-500">Sin atender por un humano</div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-2"><Clock size={18} /><span className="text-sm font-medium">Total pendiente</span></div>
            <div className="font-mono text-3xl mt-1">{counts?.total ?? "—"}</div>
            <div className="text-xs text-carbon-500">Acumulado pendiente de atender</div>
          </Card>
        </div>

        {/* Toolbar */}
        <Card className="p-4 mb-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex rounded border border-carbon-200 overflow-hidden">
              {["day", "week", "month"].map((v) => (
                <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`}
                  className={`px-3 py-1.5 text-xs ${view === v ? "bg-carbon-900 text-white" : "bg-page hover:bg-carbon-100"}`}>
                  {v === "day" ? "Día" : v === "week" ? "Semana" : "Mes"}
                </button>
              ))}
            </div>
            <div className="inline-flex items-center gap-2">
              <button onClick={() => navigate(-1)} data-testid="cal-prev" className="px-2 py-1.5 border border-carbon-200 rounded hover:bg-carbon-50"><ArrowLeft size={14} /></button>
              <span className="text-sm font-medium capitalize min-w-[200px] text-center">{headerLabel}</span>
              <button onClick={() => navigate(+1)} data-testid="cal-next" className="px-2 py-1.5 border border-carbon-200 rounded hover:bg-carbon-50"><ArrowRight size={14} /></button>
              <button onClick={() => setAnchor(new Date())} className="text-xs underline text-terracotta">Hoy</button>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <select value={filterKind} onChange={(e) => setFilterKind(e.target.value)} data-testid="cal-filter-kind"
                className="bg-page border border-carbon-200 rounded px-2 py-1.5 text-xs">
                <option value="">Todos los tipos</option>
                <option value="interview">Entrevistas</option>
                <option value="induction">Capacitaciones</option>
                <option value="other">Otros</option>
              </select>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="cal-filter-status"
                className="bg-page border border-carbon-200 rounded px-2 py-1.5 text-xs">
                <option value="">Todos los estados</option>
                <option value="pending">Pendiente</option>
                <option value="attended">Atendida</option>
                <option value="done">Finalizada</option>
                <option value="cancelled">Cancelada</option>
              </select>
            </div>
          </div>
        </Card>

        {/* Events grid */}
        {loading && <div className="text-carbon-500">Cargando...</div>}
        {!loading && (events?.length || 0) === 0 && (
          <Card className="p-10 text-center text-carbon-500">
            <CalendarBlank size={32} className="mx-auto mb-2 opacity-40" />
            <div className="text-sm">Sin eventos en este período. Genera un link de booking desde el simulador o crea uno manual.</div>
          </Card>
        )}

        {view === "month" ? (
          <MonthGrid anchor={anchor} groupedByDay={groupedByDay} onSelect={setSelectedEvent} />
        ) : (
          <DayList groupedByDay={groupedByDay} from={range.from} to={range.to} onSelect={setSelectedEvent} />
        )}
        </>)}
      </PageBody>

      {selectedEvent && (
        <EventModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onChanged={() => { setSelectedEvent(null); reload(); reloadCounts(); }}
        />
      )}
    </>
  );
}

function DayList({ groupedByDay, from, to, onSelect }) {
  const days = [];
  const cur = new Date(from);
  while (cur <= to) {
    days.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return (
    <div className="space-y-3">
      {days.map((d) => {
        const k = d.toISOString().slice(0, 10);
        const evs = groupedByDay[k] || [];
        return (
          <Card key={k} className="overflow-hidden">
            <div className="px-4 py-2 bg-carbon-50 border-b border-carbon-200 flex items-center justify-between">
              <div className="text-sm">
                <span className="font-medium capitalize">{fmtWeekday(d)}</span> · <span className="text-carbon-500">{fmtDate(d)}</span>
              </div>
              <span className="text-xs text-carbon-500">{evs.length} evento{evs.length === 1 ? "" : "s"}</span>
            </div>
            <div className="divide-y divide-carbon-100">
              {evs.length === 0 && <div className="px-4 py-3 text-xs text-carbon-400">Sin eventos</div>}
              {evs.map((e) => <EventRow key={e.id} event={e} onSelect={onSelect} />)}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function EventRow({ event, onSelect }) {
  const meta = KIND_META[event.kind] || KIND_META.other;
  const Icon = meta.icon;
  const st = STATUS_META[event.status] || STATUS_META.pending;
  const pipeName = event.pipeline_slug === "founder" ? "Founder" : event.pipeline_slug === "ambassador" ? "Ambassador" : null;
  return (
    <button onClick={() => onSelect(event)} data-testid={`cal-event-${event.id}`}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-carbon-50 text-left">
      <div className="font-mono text-xs text-carbon-500 w-12">{fmtTime(event.scheduled_at)}</div>
      <div className={`w-2 h-10 rounded ${meta.color}`} />
      <Icon size={18} className="text-carbon-500 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate flex items-center gap-1.5">
          {event.user_name || "Usuario"} · {meta.label}
          {pipeName && (
            <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${event.pipeline_slug === "founder" ? "bg-terracotta/15 text-terracotta" : "bg-forest/15 text-forest"}`}>
              {pipeName}
            </span>
          )}
          {event.stage_name && (
            <span className="text-[10px] text-carbon-500 truncate">· {event.stage_name}</span>
          )}
        </div>
        <div className="text-xs text-carbon-500 truncate">
          {event.duration_min} min{event.link ? " · 🔗 link" : ""}{event.user_public_id ? ` · ${event.user_public_id}` : ""}
          {event.attended_by ? ` · atendida por ${event.attended_by}` : ""}
        </div>
      </div>
      <Badge tone={st.tone}>{st.label}</Badge>
    </button>
  );
}

function MonthGrid({ anchor, groupedByDay, onSelect }) {
  const start = startOfMonth(anchor);
  const startDayOfWeek = (start.getDay() + 6) % 7;  // lunes=0
  const days = [];
  const first = new Date(start); first.setDate(first.getDate() - startDayOfWeek);
  for (let i = 0; i < 42; i++) {
    const d = new Date(first); d.setDate(first.getDate() + i);
    days.push(d);
  }
  const inMonth = (d) => d.getMonth() === anchor.getMonth();
  return (
    <Card className="overflow-hidden">
      <div className="grid grid-cols-7 text-[10px] uppercase tracking-wider text-carbon-500 bg-carbon-50 border-b border-carbon-200">
        {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((d) => <div key={d} className="px-2 py-2 text-center">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d, i) => {
          const k = d.toISOString().slice(0, 10);
          const evs = groupedByDay[k] || [];
          return (
            <div key={i} className={`min-h-[110px] border-r border-b border-carbon-100 p-1.5 ${inMonth(d) ? "" : "bg-carbon-50/50 opacity-50"}`}>
              <div className="text-[10px] text-carbon-500 mb-1">{d.getDate()}</div>
              <div className="space-y-1">
                {evs.slice(0, 3).map((e) => {
                  const meta = KIND_META[e.kind] || KIND_META.other;
                  return (
                    <button key={e.id} onClick={() => onSelect(e)}
                      className={`w-full text-left px-1.5 py-0.5 rounded text-[10px] text-white truncate ${meta.color}`}>
                      {fmtTime(e.scheduled_at)} · {e.user_name?.split(" ")[0] || "—"}
                    </button>
                  );
                })}
                {evs.length > 3 && <div className="text-[10px] text-carbon-500">+{evs.length - 3} más</div>}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function EventModal({ event, onClose, onChanged }) {
  const [link, setLink] = useState(event.link || "");
  const [notes, setNotes] = useState(event.notes || "");
  const [shareNotes, setShareNotes] = useState(false);
  const [busy, setBusy] = useState(false);
  const meta = KIND_META[event.kind] || KIND_META.other;
  const Icon = meta.icon;
  const pipeName = event.pipeline_slug === "founder" ? "Founder" : event.pipeline_slug === "ambassador" ? "Ambassador" : null;

  const attend = async () => {
    if (!link.trim()) { toast.error("Pega un link de reunión antes de notificar al usuario."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/calendar/events/${event.id}/attend`, {
        link: link.trim(),
        notes,
        share_notes_with_user: shareNotes,
      });
      toast.success(data.whatsapp_sent ? "Atendida y enviada por WhatsApp ✓" : "Atendida (sin envío)");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  const markDone = async () => {
    setBusy(true);
    try { await api.patch(`/calendar/events/${event.id}`, { status: "done" }); toast.success("Marcada como finalizada"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!confirm("¿Cancelar este evento?")) return;
    setBusy(true);
    try { await api.patch(`/calendar/events/${event.id}`, { status: "cancelled" }); toast.success("Cancelada"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!confirm("Eliminar el evento del calendario? Esta acción no se puede deshacer.")) return;
    setBusy(true);
    try { await api.delete(`/calendar/events/${event.id}`); toast.success("Eliminado"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-carbon-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-carbon-200 rounded-lg w-full max-w-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="section-label flex items-center gap-1"><Icon size={12} /> {meta.label}</div>
            <h2 className="font-heading text-xl mt-1 flex items-center gap-2 flex-wrap" data-testid="cal-event-user-name">
              {event.user_name || event.user_id?.slice(0, 8)}
              {pipeName && (
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${event.pipeline_slug === "founder" ? "bg-terracotta/15 text-terracotta" : "bg-forest/15 text-forest"}`}>
                  {pipeName}
                </span>
              )}
              {event.stage_name && (
                <span className="text-xs font-normal text-carbon-600">· {event.stage_name}</span>
              )}
            </h2>
            <div className="text-xs text-carbon-500">
              {fmtWeekday(event.scheduled_at)} {fmtDate(event.scheduled_at)} · {fmtTime(event.scheduled_at)} UTC · {event.duration_min} min
              {event.user_public_id && <span className="ml-1 font-mono">· {event.user_public_id}</span>}
            </div>
          </div>
          <Badge tone={(STATUS_META[event.status] || STATUS_META.pending).tone}>
            {(STATUS_META[event.status] || STATUS_META.pending).label}
          </Badge>
        </div>

        {/* Trazabilidad admin */}
        {(event.booked_via_admin || event.attended_by) && (
          <div className="mb-3 p-2 bg-carbon-50 border border-carbon-200 rounded text-xs space-y-0.5">
            {event.booked_via_admin && (
              <div data-testid="cal-event-booked-by">
                <span className="text-carbon-500">Link de booking enviado por:</span> <span className="font-mono text-carbon-700">{event.booked_via_admin}</span>
              </div>
            )}
            {event.attended_by && (
              <div data-testid="cal-event-attended-by">
                <span className="text-carbon-500">Atendida por:</span> <span className="font-mono text-carbon-700">{event.attended_by}</span>
                {event.attended_at && <span className="text-carbon-500"> · {new Date(event.attended_at).toLocaleString("es-GT")}</span>}
              </div>
            )}
          </div>
        )}

        {event.user_whatsapp && (
          <div className="text-xs text-carbon-500 mb-3">WhatsApp: <span className="font-mono">{event.user_whatsapp}</span> {event.user_id && <Link to={`/users/${event.user_id}`} className="ml-2 text-terracotta underline">Ver perfil</Link>}</div>
        )}

        <label className="block mb-3">
          <span className="section-label">Link de la reunión (Google Meet, Zoom, etc.)</span>
          <input
            value={link} onChange={(e) => setLink(e.target.value)}
            data-testid="cal-event-link-input"
            placeholder="https://meet.google.com/..."
            className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono"
          />
          <p className="text-[11px] text-carbon-500 mt-1">Al atender, este link se envía al usuario por WhatsApp.</p>
        </label>

        <label className="block mb-2">
          <span className="section-label">Notas {shareNotes ? "(se enviarán al lead)" : "internas"}</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
            data-testid="cal-event-notes-input"
            placeholder={shareNotes ? "Estas notas se enviarán al lead por WhatsApp junto al link." : "Notas privadas del equipo (no se comparten con el lead)."}
            className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" />
        </label>
        <label className="flex items-center gap-2 mb-4 text-xs text-carbon-700 cursor-pointer">
          <input type="checkbox" checked={shareNotes} onChange={(e) => setShareNotes(e.target.checked)}
            data-testid="cal-event-share-notes-toggle" />
          Compartir estas notas con el lead en el mensaje de WhatsApp
        </label>

        <div className="flex flex-wrap items-center gap-2 justify-end">
          <button onClick={remove} disabled={busy} data-testid="cal-event-delete-btn"
            className="text-xs text-signal hover:underline flex items-center gap-1">
            <Trash size={12} /> Eliminar
          </button>
          <button onClick={cancel} disabled={busy || event.status === "cancelled"} data-testid="cal-event-cancel-btn"
            className="text-xs px-3 py-1.5 border border-carbon-200 rounded hover:bg-carbon-50 disabled:opacity-40">
            Cancelar evento
          </button>
          {event.status !== "done" && (
            <button onClick={markDone} disabled={busy} data-testid="cal-event-done-btn"
              className="text-xs px-3 py-1.5 border border-carbon-200 rounded hover:bg-carbon-50">
              Marcar finalizada
            </button>
          )}
          <button onClick={onClose} disabled={busy} className="text-xs px-3 py-1.5 hover:bg-carbon-100 rounded">Cerrar</button>
          <button onClick={attend} disabled={busy || !link.trim()} data-testid="cal-event-attend-btn"
            className="text-xs px-4 py-1.5 bg-terracotta text-white rounded flex items-center gap-1 hover:bg-terracotta-hover disabled:opacity-50">
            <CheckCircle size={12} /> Atender y enviar link
          </button>
        </div>
      </div>
    </div>
  );
}


// =====================================================================
// AvailabilityTab · Configuración de horarios por servicio (entrevista / capacitación)
// =====================================================================
function AvailabilityTab() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <ServiceHoursCard kind="interview" />
      <ServiceHoursCard kind="induction" />
    </div>
  );
}

function ServiceHoursCard({ kind }) {
  const meta = KIND_META[kind];
  const Icon = meta.icon;
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/calendar/availability/${kind}`);
      setCfg(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando configuración");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const updateDay = (day, patch) => {
    setCfg((prev) => ({
      ...prev,
      weekly_hours: { ...prev.weekly_hours, [day]: { ...prev.weekly_hours[day], ...patch } },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/calendar/availability/${kind}`, {
        timezone: cfg.timezone,
        slot_duration_min: Number(cfg.slot_duration_min) || 30,
        weekly_hours: cfg.weekly_hours,
      });
      toast.success(`Horarios de ${meta.label.toLowerCase()} guardados`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error guardando"); }
    finally { setSaving(false); }
  };

  if (busy || !cfg) {
    return (
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Icon size={18} className={`text-${meta.tone}`} />
          <h3 className="font-medium">{meta.label}</h3>
        </div>
        <div className="text-xs text-carbon-500">Cargando…</div>
      </Card>
    );
  }

  return (
    <Card className="p-4" testid={`avail-card-${kind}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={18} className={`text-${meta.tone}`} />
        <h3 className="font-medium">{meta.label}</h3>
        <span className="ml-auto text-[10px] text-carbon-500">
          Slot · {cfg.slot_duration_min} min · {cfg.timezone}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <label className="block">
          <span className="section-label">Duración del slot (min)</span>
          <input type="number" min="5" max="240" step="5"
            value={cfg.slot_duration_min}
            onChange={(e) => setCfg({ ...cfg, slot_duration_min: e.target.value })}
            data-testid={`avail-${kind}-slot-duration`}
            className="mt-1 w-full bg-page border border-carbon-200 rounded px-2 py-1.5 text-sm font-mono" />
        </label>
        <label className="block">
          <span className="section-label">Zona horaria</span>
          <input type="text"
            value={cfg.timezone}
            onChange={(e) => setCfg({ ...cfg, timezone: e.target.value })}
            data-testid={`avail-${kind}-tz`}
            placeholder="America/Guatemala"
            className="mt-1 w-full bg-page border border-carbon-200 rounded px-2 py-1.5 text-sm font-mono" />
        </label>
      </div>

      <div className="space-y-1.5">
        {WEEKDAYS.map(({ key, label }) => {
          const dh = cfg.weekly_hours[key] || { enabled: false, start: "09:00", end: "18:00" };
          return (
            <div key={key} className="flex items-center gap-2 text-sm">
              <label className="flex items-center gap-1.5 w-28">
                <input type="checkbox" checked={!!dh.enabled}
                  onChange={(e) => updateDay(key, { enabled: e.target.checked })}
                  data-testid={`avail-${kind}-${key}-enabled`} />
                <span className={dh.enabled ? "" : "text-carbon-400"}>{label}</span>
              </label>
              <input type="time" value={dh.start}
                onChange={(e) => updateDay(key, { start: e.target.value })}
                disabled={!dh.enabled}
                data-testid={`avail-${kind}-${key}-start`}
                className="bg-page border border-carbon-200 rounded px-2 py-1 text-xs font-mono disabled:opacity-40" />
              <span className="text-carbon-400 text-xs">–</span>
              <input type="time" value={dh.end}
                onChange={(e) => updateDay(key, { end: e.target.value })}
                disabled={!dh.enabled}
                data-testid={`avail-${kind}-${key}-end`}
                className="bg-page border border-carbon-200 rounded px-2 py-1 text-xs font-mono disabled:opacity-40" />
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <button onClick={load} disabled={saving} data-testid={`avail-${kind}-reload`}
          className="text-xs px-3 py-1.5 border border-carbon-200 rounded hover:bg-carbon-50">
          Recargar
        </button>
        <button onClick={save} disabled={saving} data-testid={`avail-${kind}-save`}
          className="text-xs px-4 py-1.5 bg-terracotta text-white rounded hover:bg-terracotta-hover disabled:opacity-50">
          {saving ? "Guardando…" : "Guardar"}
        </button>
      </div>
    </Card>
  );
}

// =====================================================================
// BlackoutsTab · Bloqueos manuales de horarios (overrides)
// =====================================================================
function BlackoutsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/calendar/blackouts");
      setItems(data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("¿Eliminar este bloqueo? Los slots vuelven a estar disponibles.")) return;
    try {
      await api.delete(`/calendar/blackouts/${id}`);
      toast.success("Bloqueo eliminado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("es-GT", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-carbon-600">
          Estos bloqueos invalidan los horarios disponibles configurados arriba — los usuarios <strong>no podrán</strong> agendar en esos rangos.
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="blackout-new-btn"
          className="text-xs px-3 py-2 bg-terracotta text-white rounded flex items-center gap-1 hover:bg-terracotta-hover">
          <Plus size={14} /> Nuevo bloqueo
        </button>
      </div>

      <Card className="overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-carbon-50 border-b border-carbon-200 text-[10px] uppercase tracking-wider text-carbon-500">
          <div className="col-span-2">Servicio</div>
          <div className="col-span-3">Desde</div>
          <div className="col-span-3">Hasta</div>
          <div className="col-span-3">Motivo</div>
          <div className="col-span-1 text-right">—</div>
        </div>
        {loading && <div className="p-4 text-xs text-carbon-500">Cargando…</div>}
        {!loading && items.length === 0 && (
          <div className="p-10 text-center text-carbon-500 text-sm">
            <Prohibit size={28} className="mx-auto mb-2 opacity-40" />
            Sin bloqueos activos. Los horarios siguen los defaults configurados por servicio.
          </div>
        )}
        {items.map((b) => (
          <div key={b.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-carbon-100 text-sm items-center" data-testid={`blackout-row-${b.id}`}>
            <div className="col-span-2">
              <Badge tone={b.kind === "interview" ? "terracotta" : b.kind === "induction" ? "forest" : "neutral"}>
                {b.kind === "all" ? "Todos" : (KIND_META[b.kind]?.label || b.kind)}
              </Badge>
            </div>
            <div className="col-span-3 text-xs">{fmt(b.start_at)}</div>
            <div className="col-span-3 text-xs">{fmt(b.end_at)}</div>
            <div className="col-span-3 text-xs text-carbon-600 truncate">{b.reason || <span className="text-carbon-400">—</span>}</div>
            <div className="col-span-1 text-right">
              <button onClick={() => remove(b.id)} data-testid={`blackout-delete-${b.id}`}
                className="text-signal hover:underline text-xs"><Trash size={14} /></button>
            </div>
          </div>
        ))}
      </Card>

      {showCreate && <BlackoutCreateModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </>
  );
}

function BlackoutCreateModal({ onClose, onCreated }) {
  const [kind, setKind] = useState("all");
  const [startLocal, setStartLocal] = useState("");
  const [endLocal, setEndLocal] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!startLocal || !endLocal) { toast.error("Debes definir desde y hasta"); return; }
    setBusy(true);
    try {
      // datetime-local sin TZ → tratamos como hora local del navegador y enviamos ISO con offset.
      const startISO = new Date(startLocal).toISOString();
      const endISO = new Date(endLocal).toISOString();
      await api.post("/calendar/blackouts", { kind, start_at: startISO, end_at: endISO, reason: reason || null });
      toast.success("Bloqueo creado");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-carbon-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-carbon-200 rounded-lg w-full max-w-md p-6" data-testid="blackout-create-modal">
        <h2 className="font-heading text-lg mb-1">Bloquear horarios</h2>
        <p className="text-xs text-carbon-500 mb-4">
          El rango quedará marcado como NO disponible, incluso si los horarios por defecto lo permitían.
        </p>

        <label className="block mb-3">
          <span className="section-label">Servicio</span>
          <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="blackout-kind"
            className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
            <option value="all">Todos los servicios</option>
            <option value="interview">Sólo entrevistas</option>
            <option value="induction">Sólo capacitaciones</option>
          </select>
        </label>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <label className="block">
            <span className="section-label">Desde</span>
            <input type="datetime-local" value={startLocal} onChange={(e) => setStartLocal(e.target.value)}
              data-testid="blackout-start"
              className="mt-1 w-full bg-page border border-carbon-200 rounded px-2 py-2 text-sm font-mono" />
          </label>
          <label className="block">
            <span className="section-label">Hasta</span>
            <input type="datetime-local" value={endLocal} onChange={(e) => setEndLocal(e.target.value)}
              data-testid="blackout-end"
              className="mt-1 w-full bg-page border border-carbon-200 rounded px-2 py-2 text-sm font-mono" />
          </label>
        </div>
        <label className="block mb-4">
          <span className="section-label">Motivo (opcional)</span>
          <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Ej: feriado, capacitación interna, viaje"
            data-testid="blackout-reason"
            className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" />
        </label>

        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="text-xs px-3 py-1.5 hover:bg-carbon-100 rounded">Cancelar</button>
          <button onClick={submit} disabled={busy} data-testid="blackout-submit"
            className="text-xs px-4 py-1.5 bg-terracotta text-white rounded hover:bg-terracotta-hover disabled:opacity-50">
            {busy ? "Creando…" : "Crear bloqueo"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PublicLinkTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/calendar/public-booking");
    setCfg(data);
  };
  useEffect(() => { load(); }, []);

  const patch = async (changes) => {
    setSaving(true);
    try {
      const { data } = await api.put("/calendar/public-booking", changes);
      setCfg(data);
      toast.success("Guardado");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  if (!cfg) return <Card className="p-6 text-carbon-500">Cargando…</Card>;

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="section-label mb-1">Link público de agendamiento</div>
            <p className="text-sm text-carbon-500 max-w-xl">
              Un único URL permanente (no expira) para compartir masivamente. El visitante deja su nombre y WhatsApp,
              elige horario y se crea el lead automáticamente en <strong>{cfg.pipeline_slug}</strong> → etapa <strong>«{cfg.stage_name}»</strong> (origen «{cfg.lead_origin}»).
            </p>
          </div>
          <label className="flex items-center gap-2 shrink-0 cursor-pointer" data-testid="public-link-toggle">
            <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => patch({ enabled: e.target.checked })} disabled={saving} />
            <span className={`text-sm font-medium ${cfg.enabled ? "text-forest" : "text-carbon-400"}`}>{cfg.enabled ? "Activo" : "Inactivo"}</span>
          </label>
        </div>

        <div className="mt-4">
          <span className="text-[11px] uppercase tracking-wide text-carbon-500">URL público</span>
          {cfg.public_url ? (
            <div className="flex items-center gap-2 mt-1">
              <input readOnly value={cfg.public_url} data-testid="public-link-url"
                className="flex-1 bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" />
              <button onClick={() => { navigator.clipboard.writeText(cfg.public_url); toast.success("Copiado"); }}
                className="p-2 border border-carbon-200 rounded hover:border-terracotta" title="Copiar"><Copy size={15} /></button>
            </div>
          ) : (
            <div className="text-sm text-signal mt-1">Falta configurar el «Frontend Base URL» en Configuración → LLM/Sistema.</div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4 text-sm">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">Tipo de cita</span>
            <select value={cfg.kind} onChange={(e) => patch({ kind: e.target.value })} disabled={saving}
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm">
              <option value="interview">Entrevista</option>
              <option value="induction">Capacitación</option>
              <option value="other">Otro</option>
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">Duración (min)</span>
            <input type="number" min="10" step="5" defaultValue={cfg.duration_min}
              onBlur={(e) => patch({ duration_min: parseInt(e.target.value) || 30 })} disabled={saving}
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">Cupo por horario</span>
            <input type="number" min="1" max="500" defaultValue={cfg.slot_capacity} data-testid="public-slot-capacity"
              onBlur={(e) => patch({ slot_capacity: parseInt(e.target.value) || 50 })} disabled={saving}
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">Título de la página</span>
            <input defaultValue={cfg.title} onBlur={(e) => patch({ title: e.target.value })} disabled={saving}
              className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm" />
          </label>
        </div>
        <label className="block mt-3">
          <span className="text-[11px] uppercase tracking-wide text-carbon-500">Link de reunión (Zoom / Google Meet)</span>
          <input type="url" defaultValue={cfg.meeting_link || ""} data-testid="public-meeting-link"
            onBlur={(e) => patch({ meeting_link: e.target.value.trim() })} disabled={saving}
            placeholder="https://us02web.zoom.us/j/123456789  ó  https://meet.google.com/abc-defg-hij"
            className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm font-mono" />
          <span className="text-[11px] text-carbon-500 mt-1 block">
            Se enviará automáticamente en el mensaje de confirmación cuando un lead agende su cita. Déjalo vacío si el agente asignado enviará el enlace manualmente después.
          </span>
        </label>
      </Card>
    </div>
  );
}

